import React, { ChangeEvent } from 'react';
import { useStore } from '../../contexts/mobx';
import style from './style.module.scss';
import { Spin, Modal } from 'antd';
import { PlusOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import * as yup from 'yup';
import { NotificationManager } from '../../components/notification-manager';
import { HttpMethod } from '../../types';
import ImageCropModal from '../image-crop-modal';

// Module-level counter that persists across component remounts
let globalUploadSessionId = 0;

const YupImageUrl = yup.object({
    url: yup.string().required(),
});

interface Props {
    url: string | null | undefined;
    className?: string;
    onChange: (url: string | null) => void;
    onDelete: () => void;
    enableCrop?: boolean;
    allowSkipCrop?: boolean;
    aspectRatio?: number;
    isLegacyMode?: boolean; // Pass through to ImageCropModal
    /**
     * Stores the file and gives the address it is served at, null when it
     * failed and said so. Defaults to the API's picture upload.
     */
    upload?: (file: File) => Promise<string | null>;
    /** The types the file picker offers. Defaults to PNG and JPEG. */
    accept?: string;
    previewData?: {
        backgroundColor?: string | null;
        title?: string;
        titleColor?: string | null;
        subtitle?: string;
        cta?: string;
    };
}

export const ImageInput = (props: Props) => {
    const rootStore = useStore();
    const [isUploading, setIsUploading] = React.useState<boolean>(false);
    const [isModalVisible, setModalVisible] = React.useState<boolean>(false);
    const [isCropModalVisible, setCropModalVisible] = React.useState<boolean>(
        false
    );
    const [selectedImageSrc, setSelectedImageSrc] = React.useState<
        string | null
    >(null);
    const [uploadSessionId, setUploadSessionId] = React.useState<number>(0);
    const [originalFile, setOriginalFile] = React.useState<File | null>(null);
    const hiddenInputRef = React.useRef<HTMLInputElement | null>(null);

    const uploadImage = async (file: File) => {
        if (props.upload) {
            setIsUploading(true);
            try {
                const url = await props.upload(file);
                if (url) {
                    props.onChange(url);
                }
            } finally {
                setIsUploading(false);
            }
            return;
        }

        const formData = new FormData();
        formData.set('folder', 'promotions');
        formData.set('picture', file);

        setIsUploading(true);
        const response = await rootStore.makeNetworkCall(
            {
                url: '/config/picture',
                method: HttpMethod.POST,
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
            },
            YupImageUrl
        );
        setIsUploading(false);

        if (!response.err) {
            props.onChange(response.data.url);
        }
    };

    const onImageSelected = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            // User cancelled the operation
            return;
        }

        if (file.size > 2000000) {
            NotificationManager.showError('Image size should not exceed 2MB');
            return;
        }

        // Reset the input value to allow selecting the same file again
        if (hiddenInputRef.current) {
            hiddenInputRef.current.value = '';
        }

        if (props.enableCrop) {
            setOriginalFile(file);
            globalUploadSessionId += 1;
            setUploadSessionId(globalUploadSessionId);

            const reader = new FileReader();
            reader.onload = () => {
                setSelectedImageSrc(reader.result as string);
                setCropModalVisible(true);
            };
            reader.readAsDataURL(file);
        } else {
            // Upload directly without cropping
            await uploadImage(file);
        }
    };

    const handleCropComplete = async (croppedBlob: Blob) => {
        setCropModalVisible(false);
        setSelectedImageSrc(null);
        setOriginalFile(null); // Clear stored file

        // Convert blob to file and upload
        const file = new File([croppedBlob], 'cropped-image.jpg', {
            type: 'image/jpeg',
        });
        await uploadImage(file);
    };

    const handleSkipCrop = async (originalBlob: Blob) => {
        setCropModalVisible(false);
        setSelectedImageSrc(null);

        const fileToUpload = originalFile
            ? originalFile
            : new File([originalBlob], 'original-image.jpg', {
                  type: originalBlob.type,
              });

        setOriginalFile(null);
        await uploadImage(fileToUpload);
    };

    const handleCropCancel = () => {
        setCropModalVisible(false);
        setSelectedImageSrc(null);
        setOriginalFile(null);
    };

    return (
        <div className={`${style.container} ${props.className ?? ''}`}>
            {props.url ? (
                <div className={style.image_holder}>
                    <div className={style.overlay}>
                        <EyeOutlined
                            className={style.eye_svg}
                            onClick={() => setModalVisible(true)}
                        />
                        <DeleteOutlined
                            className={style.delete_svg}
                            onClick={props.onDelete}
                        />
                    </div>
                    <img src={props.url ?? ''} alt="thumbnail" />
                </div>
            ) : (
                <div
                    className={style.empty_interface}
                    onClick={() => hiddenInputRef.current?.click()}
                >
                    {isUploading ? <Spin /> : <PlusOutlined />}
                </div>
            )}
            <input
                type="file"
                name="image"
                accept={props.accept ?? 'image/png, image/jpeg'}
                className={style.hidden_input}
                ref={hiddenInputRef}
                onChange={onImageSelected}
            />
            <Modal
                open={isModalVisible}
                title="Image"
                footer={null}
                onCancel={() => setModalVisible(false)}
            >
                <img
                    alt="thumbnail"
                    className={style.full_width}
                    src={props.url ?? ''}
                />
            </Modal>
            {props.enableCrop && selectedImageSrc && (
                <ImageCropModal
                    key={uploadSessionId}
                    visible={isCropModalVisible}
                    imageSrc={selectedImageSrc}
                    aspectRatio={props.aspectRatio}
                    onCancel={handleCropCancel}
                    onComplete={handleCropComplete}
                    onSkipCrop={
                        props.allowSkipCrop ? handleSkipCrop : undefined
                    }
                    allowSkipCrop={props.allowSkipCrop}
                    isLegacyMode={props.isLegacyMode}
                    previewData={props.previewData}
                    uploadSessionId={uploadSessionId}
                />
            )}
        </div>
    );
};
